import type { ReactNode } from 'react'

import classNames from 'classnames'
import { Pencil } from 'lucide-react'

import { groupStageSummary } from '../groupStructure'
import type { GroupPlayUiConfig } from '../payload'
import type { Contributor, DraftSettings, PaymentMethod, PrizePosition, RegionSettings, TeamFormation } from '../types'
import { Banner, FOCUS_RING } from '../ui'
import { FORMATION_LABELS, PAYMENT_LABELS, STEPS, bracketLabel, capacityLine, formatDate, formatPrize } from '../wizard'

export type ReviewData = {
  gameName?: string
  name?: string
  description?: string
  hasBanner: boolean
  hasLogo: boolean
  teamFormation: TeamFormation
  playFormat?: string
  numberOfTeams?: string
  teamSize?: string | number
  draftSettings: DraftSettings
  regionSettings: RegionSettings
  isGroupPlayEnabled: boolean
  groupPlayConfig: GroupPlayUiConfig
  paymentMethod: PaymentMethod
  prizePool?: string
  positions: PrizePosition[]
  contributors: Contributor[]
  startDate?: string
  endDate?: string
}

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 py-1.5 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
    <dt className="text-light-typo-gray dark:text-dark-typo-gray">{label}</dt>
    <dd className="min-w-0 break-words font-medium text-light-typo-primary dark:text-dark-typo-primary">{children}</dd>
  </div>
)

const Missing = ({ children = 'Not set' }: { children?: ReactNode }) => <span className="text-red">{children}</span>

export const ReviewStep = ({
  data,
  onEdit,
  incompleteSteps,
  progress,
}: {
  data: ReviewData
  onEdit: (stepIndex: number) => void
  /** Steps that still need attention (indexes). */
  incompleteSteps: number[]
  /** USDC deposit progress message, if any. */
  progress?: string
}) => {
  const card = (stepIndex: number, children: ReactNode) => (
    <section
      className={classNames(
        'rounded-xl border p-4',
        incompleteSteps.includes(stepIndex) ? 'border-red/50' : 'border-light-border-primary dark:border-dark-border-primary',
      )}
      aria-labelledby={`ct-review-${stepIndex}`}
    >
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 id={`ct-review-${stepIndex}`} className="font-display text-lg font-bold text-light-typo-primary dark:text-dark-typo-primary">
          <span className="mr-2 tabular-nums text-light-typo-gray dark:text-dark-typo-gray">{stepIndex + 1}</span>
          {STEPS[stepIndex].label}
        </h3>
        <button
          type="button"
          onClick={() => onEdit(stepIndex)}
          className={classNames('inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-green hover:bg-green/10', FOCUS_RING)}
          aria-label={`Edit ${STEPS[stepIndex].label}`}
        >
          <Pencil size={12} aria-hidden /> Edit
        </button>
      </div>
      <dl>{children}</dl>
    </section>
  )

  const solo = data.teamFormation !== 'teams'
  const groupStage = data.isGroupPlayEnabled
    ? groupStageSummary({
        config: data.groupPlayConfig,
        teamFormation: data.teamFormation,
        numberOfTeams: data.numberOfTeams,
        playFormat: data.playFormat,
      })
    : null
  const totalPercent =
    data.positions.reduce((s, p) => s + (parseInt(p.percentage) || 0), 0) +
    data.contributors.reduce((s, c) => s + (parseInt(c.percentage) || 0), 0)

  return (
    <div className="grid gap-3">
      {card(
        0,
        <>
          <Row label="Game">{data.gameName || <Missing />}</Row>
          <Row label="Name">{data.name || <Missing />}</Row>
          <Row label="Images">
            {[data.hasBanner && 'Banner', data.hasLogo && 'Logo'].filter(Boolean).join(' · ') || 'Generated cover'}
          </Row>
          <Row label="Description">
            {data.description ? <span className="line-clamp-3 whitespace-pre-line">{data.description}</span> : <span className="text-light-typo-gray dark:text-dark-typo-gray">None</span>}
          </Row>
        </>,
      )}
      {card(
        1,
        <>
          <Row label="Teams">{FORMATION_LABELS[data.teamFormation]}</Row>
          {data.teamFormation === 'captain_pick' && (
            <Row label="Draft">
              {data.draftSettings.captain_selection_method === 'random' ? 'Random captains' : 'You choose captains'} ·{' '}
              {data.draftSettings.draft_order_type === 'snake' ? 'snake order' : 'linear order'}
            </Row>
          )}
          {data.teamFormation === 'region' && (
            <Row label="Regions">
              {data.regionSettings.max_subs_per_team} sub{data.regionSettings.max_subs_per_team === 1 ? '' : 's'} per team ·{' '}
              {data.regionSettings.balance_by_mmr ? 'balanced by skill' : 'not balanced'} ·{' '}
              {data.regionSettings.allow_international ? 'international allowed' : 'US only'}
            </Row>
          )}
          <Row label="Bracket">{bracketLabel(data.playFormat) || <Missing />}</Row>
          <Row label="Capacity">{capacityLine(data.teamFormation, solo ? undefined : data.numberOfTeams, data.teamSize)}</Row>
          <Row label="Group stage">
            {data.isGroupPlayEnabled ? (
              <>
                {groupStage?.text}
                {data.groupPlayConfig.structureMode === 'auto' && (data.groupPlayConfig.playInEnabled ? ' · play-in on' : ' · no play-in')}
                {` · grand final best of ${data.groupPlayConfig.grandFinalsFormat === 'bo5' ? 5 : 3}`}
                {groupStage?.error && (
                  <>
                    {' '}
                    <Missing>{groupStage.error}</Missing>
                  </>
                )}
              </>
            ) : (
              'Off'
            )}
          </Row>
        </>,
      )}
      {card(
        2,
        <>
          <Row label="Funding">{PAYMENT_LABELS[data.paymentMethod]}</Row>
          <Row label="Prize pool">{formatPrize(data.prizePool, data.paymentMethod)}</Row>
          <Row label="Split">
            {data.positions.map((p, i) => `${i + 1}${['st', 'nd', 'rd'][i] ?? 'th'} ${p.percentage || 0}%`).join(' · ')}
            {totalPercent !== 100 && <Missing>{` (totals ${totalPercent}%)`}</Missing>}
          </Row>
          {data.contributors.length > 0 && (
            <Row label="Creators">{data.contributors.map((c) => `${c.name || 'Unnamed'} ${c.percentage || 0}%`).join(' · ')}</Row>
          )}
        </>,
      )}
      {card(
        3,
        <>
          <Row label="Starts">{data.startDate ? formatDate(data.startDate) : <Missing />}</Row>
          <Row label="Ends">{data.endDate ? formatDate(data.endDate) : <Missing />}</Row>
        </>,
      )}

      {(data.paymentMethod === 'leet_token' || data.paymentMethod === 'coinbase_usdc') && (
        <Banner>
          {data.paymentMethod === 'coinbase_usdc'
            ? 'When you select Create tournament, your wallet switches to Base and asks you to approve and deposit the USDC prize pool. Keep this window open until it finishes.'
            : 'When you select Create tournament, your wallet asks you to approve and then deposit the LEET prize pool.'}
        </Banner>
      )}

      {progress && (
        <div role="status" className="flex items-center gap-3 rounded-lg border border-btn-fancy-primary-end/40 bg-btn-fancy-primary-end/[0.06] px-3 py-2.5 text-sm text-light-typo-primary dark:text-dark-typo-primary">
          <span aria-hidden className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-green border-t-transparent motion-reduce:animate-none" />
          {progress}
        </div>
      )}
    </div>
  )
}
